/**
 * Scheduler komunikatów godzinowych - czysta logika, bez Reacta i bez audio.
 *
 * Zasada emisji: komunikat nie przerywa utworu. Gdy minie pełna godzina,
 * scheduler zgłasza, że komunikat jest „należny", a kolejka wstawia go jako
 * następną pozycję po aktualnie grającym utworze (np. utwór kończy się o 14:03
 * → „Jest godzina czternasta" → kolejny utwór). Bez synchronizacji co do sekundy.
 */

import { buildTimeAnnouncement, getTimeAnnouncementAudioUrl } from './timeAnnouncements';

const HOUR_MS = 60 * 60 * 1000;

export interface HourlyAnnouncement {
  hour: number;
  /** Treść do odczytania przez lektora / TTS. */
  text: string;
  /** Gdzie powinien leżeć plik audio komunikatu. */
  audioUrl: string;
}

/** Ile milisekund zostało do najbliższej pełnej godziny. */
export function getMsUntilNextHour(now: Date = new Date()): number {
  const elapsed = now.getMinutes() * 60_000 + now.getSeconds() * 1000 + now.getMilliseconds();
  return HOUR_MS - elapsed;
}

/** Klucz godziny zegarowej (lokalnej), np. `2026-10-05T14`. */
export function getHourKey(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}`;
}

/** Komunikat odpowiadający danej godzinie (0–23). */
export function getHourlyAnnouncement(hour: number): HourlyAnnouncement {
  return {
    hour,
    text: buildTimeAnnouncement(hour),
    audioUrl: getTimeAnnouncementAudioUrl(hour),
  };
}

export interface HourlyScheduler {
  msUntilNextHour(): number;
  /** Czy minęła pełna godzina, której komunikat jeszcze nie został obsłużony. */
  isDue(): boolean;
  /** Komunikat należny w tej chwili albo `null`. */
  getDueAnnouncement(): HourlyAnnouncement | null;
  /** Czy w bieżącej godzinie zegarowej komunikat został już wyemitowany. */
  wasAnnouncedThisHour(): boolean;
  /** Odnotowuje emisję (albo świadome pominięcie) komunikatu bieżącej godziny. */
  markAnnounced(): void;
}

/**
 * @param now źródło czasu (wstrzykiwane w testach)
 *
 * Godzina, w której słuchacz włączył radio, nie jest zaległa: komunikat należy
 * się dopiero po przekroczeniu pierwszej pełnej godziny w trakcie sesji.
 */
export function createHourlyScheduler(now: () => Date = () => new Date()): HourlyScheduler {
  const sessionStartKey = getHourKey(now());
  const announced = new Set<string>();

  const isDue = (): boolean => {
    const key = getHourKey(now());
    return key !== sessionStartKey && !announced.has(key);
  };

  return {
    msUntilNextHour: () => getMsUntilNextHour(now()),
    isDue,
    getDueAnnouncement: () => (isDue() ? getHourlyAnnouncement(now().getHours()) : null),
    wasAnnouncedThisHour: () => announced.has(getHourKey(now())),
    markAnnounced: () => {
      announced.add(getHourKey(now()));
    },
  };
}
